import { Link } from "react-router-dom";
import {
  Globe2, MessageCircle, ShieldCheck, HeartHandshake, Stethoscope,
  FileText, Eye, Sparkles, BadgeCheck, Languages, Landmark, ChevronRight,
} from "lucide-react";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { CLINIC, waLink } from "../lib/clinic";
import {
  SITE, AUTHOR_REF, AUTHOR_REF_MANISHA, PUBLISHER_REF, breadcrumb, graph,
} from "../lib/schema";
import MedicalDisclaimer from "../components/site/MedicalDisclaimer";
import SymptomCheckerTeaser from "../components/site/SymptomCheckerTeaser";

// International Patients landing page — Feb 2026.
// Design language references: Mayo Clinic / Moorfields / Cleveland Clinic —
// minimal, elegant, ethics-first. Every claim is factual and evidence-based:
// no "best in Asia" superlatives, no rate cards, no marketing gimmicks.

const DR_SHANTANU_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_shantanu-netralaya/artifacts/cyab0eqg_6BF292C3-6EEB-49C8-BD2E-1008EDDDC0BD%202.PNG";
const DR_MANISHA_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_shantanu-netralaya/artifacts/4ykhz87v_F43F41D7-9C9C-464E-90EE-6356CEF829FD.png";

// Direct international WhatsApp link per user brief — +91 79058 54244.
const INTL_WA = `https://wa.me/917905854244?text=${encodeURIComponent(
  "Hello Dr. Shantanu, I am an international patient interested in eye care at Shantanu Netralaya, Varanasi."
)}`;

const WHY_US = [
  { icon: HeartHandshake, title: "Personalized treatment plans", body: "Every case is worked up individually — no cookie-cutter surgical packages." },
  { icon: Stethoscope, title: "Compassionate, doctor-led care", body: "You are seen and counselled by the operating consultant — not routed through a coordinator." },
  { icon: ShieldCheck, title: "Ethical recommendations", body: "We recommend surgery only when it is genuinely needed. Second opinions are welcomed." },
  { icon: FileText, title: "Transparent communication", body: "Written summaries, itemised costs and clear expectations at every step." },
  { icon: Eye, title: "Comprehensive eye evaluation", body: "A single-visit workup covers refraction, biometry, OCT, and posterior-segment examination." },
  { icon: Sparkles, title: "Advanced diagnostics", body: "OCT, IOL Master 700, corneal topography, fundus photography and B-scan available in-house." },
  { icon: BadgeCheck, title: "Modern cataract surgery", body: "Micro-incision phacoemulsification (2.2 mm) with monofocal, toric, EDOF and trifocal IOL options." },
  { icon: HeartHandshake, title: "Comfortable patient experience", body: "Unhurried consultations, quiet clinical areas, and same-day discharge for most cataract cases." },
  { icon: Languages, title: "English-speaking assistance", body: "Consultations, written reports and discharge instructions are provided in English on request." },
  { icon: MessageCircle, title: "Digital reports & follow-up", body: "Reports emailed as PDFs; short queries answered on WhatsApp so distance never blocks aftercare." },
];

const WHAT_WE_TREAT = [
  { title: "Cataract Surgery", body: "MICS phaco with the full IOL portfolio — monofocal, aspheric, toric, EDOF and trifocal." },
  { title: "Medical Retina", body: "Wet AMD, diabetic macular oedema, retinal vein occlusion, central serous retinopathy." },
  { title: "Diabetic Eye Disease", body: "OCT-guided screening, intravitreal anti-VEGF injections and pan-retinal laser (PRP)." },
  { title: "Glaucoma", body: "Diagnosis, IOP monitoring, medical management and long-term perimetry follow-up." },
  { title: "Cornea & Ocular Surface", body: "Dry eye, corneal ulcers, contact-lens intolerance and pre-transplant assessment." },
  { title: "Squint & Pediatric", body: "Adult and pediatric strabismus, lazy eye (amblyopia) therapy and refractive-error management." },
  { title: "Uveitis", body: "Systematic uveitis workup with etiology-directed medical therapy." },
  { title: "Eye Emergencies", body: "Same-day evaluation for sudden vision loss, ocular trauma and post-operative concerns." },
];

const COMMITMENTS = [
  { title: "No upselling", body: "You will never be pushed toward a premium IOL, procedure or investigation you don't need. Standard options are always presented alongside premium ones." },
  { title: "Transparent pricing", body: "You receive a written, itemised quote in advance — with the standard-tier option quoted first. There are no hidden add-ons at admission." },
  { title: "Honest second opinions", body: "If your current treatment plan is appropriate, we say so — in writing — and encourage you to continue with your existing doctor." },
  { title: "Evidence-based practice", body: "Recommendations follow the American Academy of Ophthalmology (AAO) preferred practice patterns and Royal College of Ophthalmologists guidance." },
];

const FAQS = [
  { q: "Can I have a video consultation before travelling to India?", a: "Yes. We offer paid pre-travel video consultations for international patients. Please share any previous reports over WhatsApp beforehand; the consultation itself is approximately 30 minutes and includes a written summary emailed to you afterwards." },
  { q: "What language will my consultation and reports be in?", a: "Consultations, printed medical reports and post-operative instructions are provided in English on request. Both consultants are fluent in English; the front-desk team also assists in English throughout your visit." },
  { q: "How soon after cataract surgery can I fly home?", a: "In most uncomplicated cases, international flights are safe after the first-day post-operative check — typically 24 to 48 hours after surgery. We provide a fitness-to-fly note when required by your airline or insurer." },
  { q: "Will I receive my medical reports digitally?", a: "Yes. All investigation reports (OCT, biometry, fundus photographs, discharge summary) are shared as PDFs by email. Physical copies are also handed over at discharge." },
  { q: "Can I continue follow-up with you after returning to my country?", a: "For routine post-operative questions we respond over WhatsApp and email. If a physical examination is required, we help arrange onward care with an ophthalmologist in your country and share your full record with them, with your consent." },
  { q: "Do you accept international insurance?", a: "Most Indian eye hospitals — including ours — operate on a pay-and-claim basis with international insurers rather than cashless. We provide fully itemised invoices, standardised procedure codes and medical summaries so your insurer can reimburse you after you return home. Please confirm your policy terms with your insurer in advance." },
  { q: "Will the operating surgeon actually see me — or a trainee?", a: "Every international patient is personally seen, examined and counselled by the consultant who will perform the surgery. Trainees are not involved in international-patient consultations." },
  { q: "Is it safe to combine treatment with sightseeing in Varanasi?", a: "For a routine cataract patient the answer is yes for gentle activity — a walk along the ghats or a boat ride, in daylight, with sunglasses — after the first-day check. For retina injections, glaucoma workups or squint surgery the recovery plan may differ; we advise this individually at consultation." },
];

const InternationalPatients = () => {
  usePageMeta({
    title: "International Patients | Shantanu Netralaya Eye Hospital, Varanasi",
    path: "/international-patients",
    description:
      "Ethical, English-language eye care for international patients visiting Varanasi (Kashi). Cataract, retina, glaucoma, pediatric, squint, cornea and uveitis services with transparent pricing, digital reports and post-return WhatsApp follow-up.",
  });

  usePageJsonLd(
    graph([
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "International Patients", path: "/international-patients" },
      ]),
      {
        "@type": "MedicalWebPage",
        "@id": `${SITE}/international-patients#page`,
        url: `${SITE}/international-patients`,
        name: "International Eye Care in the Heart of Varanasi",
        description:
          "Advanced technology, experienced ophthalmologists, ethical medical practice, and personalised care for international patients visiting India.",
        inLanguage: "en-IN",
        author: AUTHOR_REF,
        reviewedBy: [AUTHOR_REF, AUTHOR_REF_MANISHA],
        publisher: PUBLISHER_REF,
        specialty: { "@type": "MedicalSpecialty", name: "Ophthalmology" },
        audience: {
          "@type": "MedicalAudience",
          audienceType: "International patients",
          geographicArea: { "@type": "Place", name: "Varanasi, India" },
        },
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: [".intl-hero-lead", ".intl-faq-answer"],
        },
      },
      {
        "@type": "FAQPage",
        "@id": `${SITE}/international-patients#faq`,
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ])
  );

  return (
    <main data-testid="international-patients-page" className="pb-24">
      {/* ─────────────────────────── HERO ─────────────────────────── */}
      <section className="pt-16 sm:pt-24 pb-14 container-page">
        <div className="grid lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] gap-14 lg:gap-20 items-start">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--accent))]" data-testid="intl-overline">
              <Globe2 size={13} strokeWidth={1.75} />
              International Patients · Varanasi
            </div>
            <h1
              className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance text-[hsl(var(--foreground))]"
              data-testid="intl-h1"
            >
              International Eye Care in the Heart of Varanasi.
            </h1>
            <p className="intl-hero-lead mt-8 prose-elegant max-w-2xl" data-testid="intl-hero-lead">
              Advanced technology, experienced ophthalmologists, ethical medical practice and
              personalised care for international patients visiting India. Every recommendation is
              factual, evidence-based and communicated in writing — the way medicine ought to be
              practised.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={INTL_WA}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="intl-whatsapp-cta"
                className="btn-primary !py-3.5"
              >
                <MessageCircle size={17} strokeWidth={1.6} aria-hidden="true" />
                WhatsApp Dr. Shantanu
              </a>
              <a
                href={`mailto:${CLINIC.email}?subject=International%20Patient%20Enquiry`}
                data-testid="intl-email-cta"
                className="btn-ghost !py-3.5"
              >
                Email the clinic
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-xs uppercase tracking-[0.2em] text-[hsl(var(--foreground))]/70">
              <span className="inline-flex items-center gap-2"><BadgeCheck size={13} className="text-[hsl(var(--accent))]" strokeWidth={2} /> MAMC · New Delhi trained</span>
              <span className="inline-flex items-center gap-2"><BadgeCheck size={13} className="text-[hsl(var(--accent))]" strokeWidth={2} /> DNB · FICO (Merit)</span>
              <span className="inline-flex items-center gap-2"><BadgeCheck size={13} className="text-[hsl(var(--accent))]" strokeWidth={2} /> 10,000+ cataract surgeries</span>
              <span className="inline-flex items-center gap-2"><BadgeCheck size={13} className="text-[hsl(var(--accent))]" strokeWidth={2} /> 4.8★ · 900+ Google reviews</span>
            </div>
          </div>

          {/* Portrait montage — quiet, editorial */}
          <div className="relative hidden lg:block">
            <div className="grid grid-cols-2 gap-4">
              <img
                src={DR_SHANTANU_PORTRAIT}
                alt="Dr. Shantanu Kumar Gupta, cataract & medical retina specialist"
                className="w-full rounded-md object-cover aspect-[3/4] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.35)]"
                loading="eager"
              />
              <img
                src={DR_MANISHA_PORTRAIT}
                alt="Dr. Manisha Mishra, pediatric ophthalmology & squint specialist"
                className="w-full rounded-md object-cover aspect-[3/4] mt-10 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.35)]"
                loading="eager"
              />
            </div>
            <div className="absolute -bottom-6 left-2 right-8 bg-background border border-border rounded-md px-5 py-3 flex items-center justify-between text-xs uppercase tracking-[0.18em] text-[hsl(var(--foreground))]/70 shadow-[0_10px_30px_-15px_rgba(0,0,0,0.25)]">
              <span>Shantanu Netralaya</span>
              <span className="text-[hsl(var(--accent))]">Durgakund · Varanasi</span>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────── WHY INTERNATIONAL PATIENTS ─────────────────── */}
      <section className="border-t border-border container-page pt-16 pb-14" data-testid="intl-why">
        <div className="max-w-3xl">
          <div className="overline">What sets us apart</div>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl leading-tight text-balance">
            Why international patients choose Shantanu Netralaya.
          </h2>
          <p className="mt-5 prose-elegant">
            We are a boutique, doctor-led practice — not a chain hospital. That shapes every
            aspect of how we care for international patients: unhurried consultations, honest
            communication, and clinical decisions taken by the operating consultant rather than a
            coordinator.
          </p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
          {WHY_US.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.title} className="pl-4 border-l-2 border-[hsl(var(--accent))]/40">
                <Icon size={20} strokeWidth={1.5} className="text-[hsl(var(--accent))]" aria-hidden="true" />
                <h3 className="mt-3 font-serif text-lg text-[hsl(var(--foreground))]">{item.title}</h3>
                <p className="mt-2 text-[14px] leading-relaxed text-[hsl(var(--foreground))]/75">{item.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ─────────────────── WHAT WE TREAT ─────────────────── */}
      <section className="border-t border-border container-page pt-16 pb-14" data-testid="intl-treat">
        <div className="max-w-3xl">
          <div className="overline">Clinical services</div>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl leading-tight text-balance">
            What we treat, under one roof.
          </h2>
          <p className="mt-5 prose-elegant">
            The eight sub-specialties below cover the majority of adult and paediatric eye
            problems. If your condition sits outside our scope we will say so honestly and
            refer you to a colleague better placed to help.
          </p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {WHAT_WE_TREAT.map((s) => (
            <div
              key={s.title}
              className="card-elegant p-6 flex flex-col"
              data-testid={`intl-service-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <h3 className="font-serif text-lg text-[hsl(var(--foreground))]">{s.title}</h3>
              <p className="mt-2 text-[13.5px] leading-relaxed text-[hsl(var(--foreground))]/75">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─────────────────── COMBINING WITH KASHI ─────────────────── */}
      <section className="border-t border-border container-page pt-16 pb-14" data-testid="intl-kashi">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-12 items-start">
          <div>
            <div className="overline flex items-center gap-2"><Landmark size={13} strokeWidth={1.75} /> Combining care with Kashi</div>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl leading-tight text-balance">
              A treatment visit that respects both medicine and pilgrimage.
            </h2>
          </div>
          <div className="prose-elegant">
            <p>
              Varanasi — Kashi — is one of the oldest continuously-inhabited cities in the world.
              Many of our international patients time their eye care around a visit to the ghats,
              Sarnath, or an early-morning boat ride along the Ganga. After a routine cataract
              procedure, gentle daylight activity with protective sunglasses is usually possible
              from the day following surgery.
            </p>
            <p className="mt-4">
              We do not organise travel or accommodation ourselves — that is deliberate. Our role
              is to look after your eyes properly, and to advise which activities are safe for
              your individual recovery timeline. Everything else, you plan on your own terms.
            </p>
          </div>
        </div>
      </section>

      {/* ─────────────────── OUR SPECIALISTS ─────────────────── */}
      <section className="border-t border-border container-page pt-16 pb-14" data-testid="intl-specialists">
        <div className="max-w-3xl">
          <div className="overline">Our specialists</div>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl leading-tight text-balance">
            The consultants who will personally see you.
          </h2>
          <p className="mt-5 prose-elegant">
            International patients are personally consulted, operated on and followed up by the
            named consultant below — not a trainee or a coordinator standing in for them.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-10">
          {/* Dr. Shantanu */}
          <article className="card-elegant overflow-hidden" data-testid="intl-specialist-shantanu">
            <div className="grid grid-cols-[minmax(0,140px)_minmax(0,1fr)] gap-6 p-6 sm:p-8">
              <img
                src={DR_SHANTANU_PORTRAIT}
                alt="Dr. Shantanu Kumar Gupta"
                className="w-full aspect-[3/4] object-cover rounded-md"
                loading="lazy"
              />
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent))]">Senior consultant</div>
                <h3 className="mt-2 font-serif text-2xl text-[hsl(var(--foreground))]">Dr. Shantanu Kumar Gupta</h3>
                <p className="mt-1 text-[13px] uppercase tracking-[0.14em] text-[hsl(var(--foreground))]/70">
                  Cataract &amp; Medical Retina Specialist
                </p>
                <p className="mt-3 text-[13.5px] text-[hsl(var(--foreground))]/80 leading-relaxed">
                  MBBS · MS (MAMC, New Delhi) · DNB · FICO (Merit). Personally responsible for
                  over 10,000 cataract surgeries and the medical-retina practice at the clinic.
                </p>
              </div>
            </div>
            <div className="border-t border-border/60 px-6 sm:px-8 py-5">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--foreground))]/60 mb-3">Sub-specialties</div>
              <ul className="grid grid-cols-2 gap-x-6 gap-y-2 text-[13.5px] text-[hsl(var(--foreground))]/80">
                {["Cataract Surgery","Medical Retina","Diabetic Retinopathy","Macular Diseases","Intravitreal Injections","Retinal Laser"].map((s) => (
                  <li key={s} className="flex items-start gap-2">
                    <ChevronRight size={13} className="mt-1 text-[hsl(var(--accent))] shrink-0" strokeWidth={2} />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/about"
                className="mt-5 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[hsl(var(--primary))] hover:underline"
                data-testid="intl-specialist-shantanu-more"
              >
                Full biography <ChevronRight size={13} strokeWidth={2} />
              </Link>
            </div>
          </article>

          {/* Dr. Manisha */}
          <article className="card-elegant overflow-hidden" data-testid="intl-specialist-manisha">
            <div className="grid grid-cols-[minmax(0,140px)_minmax(0,1fr)] gap-6 p-6 sm:p-8">
              <img
                src={DR_MANISHA_PORTRAIT}
                alt="Dr. Manisha Mishra"
                className="w-full aspect-[3/4] object-cover rounded-md"
                loading="lazy"
              />
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent))]">Senior consultant</div>
                <h3 className="mt-2 font-serif text-2xl text-[hsl(var(--foreground))]">Dr. Manisha Mishra</h3>
                <p className="mt-1 text-[13px] uppercase tracking-[0.14em] text-[hsl(var(--foreground))]/70">
                  Cataract, Pediatric Ophthalmology &amp; Squint Specialist
                </p>
                <p className="mt-3 text-[13.5px] text-[hsl(var(--foreground))]/80 leading-relaxed">
                  MBBS (Gold Medalist, 4 distinctions) · MS (MAMC, New Delhi) · DNB · FICO (Merit).
                  Leads paediatric ophthalmology, strabismus surgery and complex-cataract cases.
                </p>
              </div>
            </div>
            <div className="border-t border-border/60 px-6 sm:px-8 py-5">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--foreground))]/60 mb-3">Sub-specialties</div>
              <ul className="grid grid-cols-2 gap-x-6 gap-y-2 text-[13.5px] text-[hsl(var(--foreground))]/80">
                {["Pediatric Eye Care","Squint Surgery","Lazy Eye (Amblyopia)","Adult Squint","Complex Cataracts","Refractive Error"].map((s) => (
                  <li key={s} className="flex items-start gap-2">
                    <ChevronRight size={13} className="mt-1 text-[hsl(var(--accent))] shrink-0" strokeWidth={2} />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/team/dr-manisha-mishra"
                className="mt-5 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[hsl(var(--primary))] hover:underline"
                data-testid="intl-specialist-manisha-more"
              >
                Full biography <ChevronRight size={13} strokeWidth={2} />
              </Link>
            </div>
          </article>
        </div>
      </section>

      {/* ─────────────────── OUR COMMITMENTS ─────────────────── */}
      <section className="border-t border-border container-page pt-16 pb-14" data-testid="intl-commitments">
        <div className="grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-12 items-start">
          <div>
            <div className="overline">Our commitments</div>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl leading-tight text-balance">
              How we practise — put in writing.
            </h2>
            <p className="mt-5 prose-elegant">
              Every international patient receives these four commitments in the welcome note
              that goes with their appointment confirmation. They are the standards we hold
              ourselves to.
            </p>
          </div>
          <ol className="space-y-6">
            {COMMITMENTS.map((c, i) => (
              <li key={c.title} className="grid grid-cols-[auto_minmax(0,1fr)] gap-5">
                <div className="font-serif text-3xl text-[hsl(var(--accent))] leading-none w-10">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div>
                  <h3 className="font-serif text-lg text-[hsl(var(--foreground))]">{c.title}</h3>
                  <p className="mt-2 text-[14px] leading-relaxed text-[hsl(var(--foreground))]/75">{c.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ─────────────────── FAQ ─────────────────── */}
      <section className="border-t border-border container-page pt-16 pb-14" data-testid="intl-faq">
        <div className="max-w-3xl">
          <div className="overline">Frequently asked</div>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl leading-tight text-balance">
            Practical questions from international patients.
          </h2>
        </div>
        <div className="mt-10 max-w-4xl divide-y divide-border border-t border-border">
          {FAQS.map((f, i) => (
            <details
              key={f.q}
              className="group py-5"
              data-testid={`intl-faq-${i}`}
              {...(i === 0 ? { open: true } : {})}
            >
              <summary className="cursor-pointer flex items-start justify-between gap-6 text-[15px] font-medium text-[hsl(var(--foreground))] list-none">
                <span>{f.q}</span>
                <ChevronRight
                  size={16}
                  className="mt-1 shrink-0 text-[hsl(var(--accent))] transition-transform group-open:rotate-90"
                  strokeWidth={2}
                />
              </summary>
              <p className="intl-faq-answer mt-4 text-[14.5px] leading-relaxed text-[hsl(var(--foreground))]/80">
                {f.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* ─────────────────── CTA FOOTER ─────────────────── */}
      <section className="mt-16" data-testid="intl-cta">
        <div className="container-page">
          <div className="rounded-lg border border-border bg-[hsl(var(--primary))]/[0.04] px-8 py-12 sm:px-14 sm:py-14 grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-10 items-center">
            <div>
              <div className="overline">Start with a message</div>
              <h2 className="mt-4 font-serif text-2xl sm:text-3xl leading-tight text-balance">
                Share your reports on WhatsApp — we&apos;ll come back to you personally.
              </h2>
              <p className="mt-4 prose-elegant">
                Most international enquiries begin with a WhatsApp message. Attach any existing
                reports (OCT, prescriptions, previous surgeon&apos;s notes) and describe the
                problem in your own words. Dr. Shantanu personally reviews international messages
                and typically replies within one working day.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <a
                href={INTL_WA}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary justify-center"
                data-testid="intl-cta-whatsapp"
              >
                <MessageCircle size={16} strokeWidth={1.6} /> Message on WhatsApp
              </a>
              <a
                href={`mailto:${CLINIC.email}?subject=International%20Patient%20Enquiry`}
                className="btn-ghost justify-center"
                data-testid="intl-cta-email"
              >
                Or write to {CLINIC.email}
              </a>
              <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--foreground))]/60 text-center">
                WhatsApp · +91 79058 54244
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="container-page mt-14">
        <SymptomCheckerTeaser context="international-patients" />
      </div>

      <div className="container-page mt-14">
        <MedicalDisclaimer />
      </div>
    </main>
  );
};

export default InternationalPatients;
